

















import { computed, defineComponent, onUnmounted, ref, watch } from "@vue/composition-api";
import { isEqual } from "lodash-es";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import {
  ContentFeed,
  InputContentFeedSearch,
} from "@scrile/api-provider/dist/projects/scrileConnect/ContentFeedProvider";
import useMySubscriptionLevels from "@/hooks/useMySubscriptionLevels";
import useNSFWUserSettings from "@/hooks/useNSFWUserSettings";
import usePager from "@/hooks/usePager";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import useUserFollowings from "@/hooks/useUserFollowings";
import providers from "@/lib/api";
import { getPurchasedContentFeeds } from "@/lib/contentFeedHelper";
import emitter from "@/lib/emitter";
import { feedFields } from "@/lib/fieldsHelper";
import { EVENTS, GRID_PAGE_SIZE } from "@/const";
import LikedContentView from "./components/LikedContentView";
import { filterItems } from "./const";

export default defineComponent({
  name: "PageLikedContent",
  components: { LikedContentView },
  props: {
    feedId: { type: String },
    type: { type: String, default: "all" },
  },
  setup(props) {
    const { showNotSafeForWork } = useNSFWUserSettings();
    const { getMySubscriptionLevels } = useMySubscriptionLevels();
    const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.FILESET]);

    const loading = ref(false);

    const filterData = computed(() => ({
      safeForWork: showNotSafeForWork.value ? undefined : true,
      types:
        filterItems.value.length > 2
          ? (filterItems.value.find(i => i.name === props.type)?.value ?? filterItems.value[0].value)
          : filterItems.value[0].value,
    }));

    const { getFirstPage, getNextPage, data, items, enableScroll, updateItem, getLostItem, removeItem } = usePager<
      ContentFeed,
      InputContentFeedSearch
    >((page, data) =>
      providers.ContentFeedProvider.search(
        {
          data: {
            page,
            published: true,
            isLiked: true,
            sort: "LIKE_TIME,DESC;ID",
            size: GRID_PAGE_SIZE,
            ...filterData.value,
            ...data,
          },
        },
        feedFields()
      )
    );

    const { followedUserIds, handleToggleFollow } = useUserFollowings(items);

    const onContentFeedUpdate = (contentFeed: ContentFeed) => {
      updateItem(contentFeed, (i1, i2) => i1.id === i2.id);
    };

    const updateFeed = async (payload: { userId: string; feedId?: string }) => {
      if (!data.value) return;
      try {
        data.value = {
          ...data.value,
          result: await getPurchasedContentFeeds(
            { authorId: payload.userId, purchasedFeedId: payload.feedId },
            data.value,
            undefined,
            feedFields()
          ),
        };
      } finally {
        if (!payload.feedId) {
          handleToggleFollow(payload.userId, true);
        }
      }
    };

    const onToggleLike = ({ status }: { status: boolean }) => {
      if (status) {
        data.value?.hasNextPage && data.value.result.splice(-1, 1);
      } else {
        const unlikedPostsCount = items.value.filter(i => !i.likes.liked).length;
        getLostItem(undefined, { page: items.value.length - unlikedPostsCount });
      }
    };

    const onDeleteFeed = (item: ContentFeed) => {
      removeItem(i => i.id === item.id);
    };

    const loadContent = async () => {
      try {
        loading.value = true;
        await getFirstPage();
      } finally {
        window.scrollTo({ top: 0 });
        loading.value = false;
      }
    };

    const getData = async () => {
      await Promise.all([getPaymentPackages(), getMySubscriptionLevels()]);
    };

    getData();

    watch(
      filterData,
      (newValue, oldValue) => {
        if (isEqual(newValue, oldValue)) return;
        loadContent();
      },
      { immediate: true }
    );

    emitter.on<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
    emitter.on<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
    onUnmounted(() => {
      emitter.off<any>(EVENTS.CONTENT_FEED_UPDATED, updateFeed);
      emitter.off<any>(EVENTS.CONTENT_FEED_PURCHASED, updateFeed);
    });

    return {
      loading,
      data,
      paymentPackages,
      followedUserIds,
      enableScroll,
      getNextPage,
      onContentFeedUpdate,
      onToggleLike,
      onDeleteFeed,
      handleToggleFollow,
    };
  },
});
